import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, TrendingUp, Percent, Info, Users, UserPlus, Image as ImageIcon, CalendarDays, Clock,
  AlertTriangle, BarChart2, Telescope, Table2,
} from 'lucide-react';
import NotFoundTargetsCard from './NotFoundTargetsCard';
import CloseFriendsCard from './stats/CloseFriendsCard';
import SavedCollectionCard from './stats/SavedCollectionCard';
import TikTokStatsCard from './stats/TikTokStatsCard';
import FollowBackCard from './stats/FollowBackCard';
import StoryShareCard from './stats/StoryShareCard';
import { r15UiEnabled, r16mUiEnabled, r16nUiEnabled } from '../lib/r14Switch';
import { slotStatsApi, slotsApi } from '../lib/api';
import { slotPlatform } from '../lib/platform';
import { normaliseSlotStats, daysBetween } from '../lib/slotHistory';
import {
  RANGES, CHART_PROJECTION_DAYS, WEEKLY_FROM_RANGE, rangeDays, growthPoints, growthTrack, headline, actionRows,
  actionSummary, actionBars, actionStack, toggleHidden, lateStarts, actionTotals, dailyTable, projection, projectionLine,
  rangeRatios, changeBars, newestDay, partialDay, fmtDay, fmtSpan, fmtInt, fmtSigned, fmtRate, fmtServerDateTime, plural,
} from '../lib/slotStatsView';
import { classifyR14Error, r14ErrorText } from '../lib/r14Errors';
import {
  ChartCard, DataTableView, Legend, Segmented, EmptyPanel,
} from './stats/ChartKit';
import { GrowthChart, DailyChangeChart } from './stats/GrowthCharts';
import {
  ActionsChart, ActionsStack, ActionTotalsTable, MetricTabs,
} from './stats/ActionsCharts';
import { actionColor } from './stats/ChartKit';
import { HeadlineTile, RatioTiles, ProjectionBlock } from './stats/StatTiles';
import DailyTable from './stats/DailyTable';
import './stats/stats.css';

/*
 * Round 14: the Slot Stats page body. Round 14b (owner, 2026-09-29: "look at some great stats
 * websites") redrew it the way a stats site reads (Social Blade per account):
 *
 *   1. the page header: the slot's name, the time range (7 / 30 / 90 days) that scopes everything
 *      below it, when the profile was last checked and how old the data is;
 *   2. headline tiles - followers, following, posts: value, change over the range (+ %), per day,
 *      the period before, sparkline;
 *   3. the follower count over time (with the projection as a dashed estimate), following and posts
 *      as their own small charts (never on the follower axis);
 *   4. follower change as diverging bars (weekly at 90 days), and a projection clearly marked as an
 *      estimate;
 *   5. actions done: metric tabs with each type's total; "All actions" stacks every type (a legend
 *      that hides / shows and highlights types), a type's tab charts it alone with its average; and
 *      the today / 7 / 30 / 90-day / average totals table;
 *   6. the actions-done ratios over the range as defined stat tiles;
 *   7. a daily table, newest first, with the range's totals and averages;
 * and the not-found targets card, unchanged.
 *
 * Round 15 (2026-09-30; switch r15UiEnabled in lib/r14Switch.js): above the not-found card, the Close Friends
 * card (components/stats/CloseFriendsCard.js - its day-by-day chart follows the range above and reads the
 * closeFriends* fields of this page's /stats answer, which stay out of every action total, the stack and the
 * ratios) and the saved-collection card (components/stats/SavedCollectionCard.js). Each has its own route and
 * is drawn only when the server answers it: nothing at all on a server before round 15 or with the part off.
 *
 * Round 16n (2026-10-21; switch r16nUiEnabled): right under "Actions done", the Story shares card (components/stats/StoryShareCard.js):
 * story shares by audience - Your story (everyone) vs Close Friends - from the storyShares* fields of this page's /stats answer, on
 * their own two-colour stack (never in the actions stack, its tabs, totals, the daily table or the ratios). Instagram slots only;
 * nothing on a server before r16n, with its storyShareStats switch off, or for a slot that has recorded no share.
 *
 * Every chart has titled axes, a hover / keyboard tooltip and a table twin. Every figure is from
 * GET /uiapi/slots/{slot}/stats (lib/slotHistory.js has the contract, lib/slotStatsView.js the
 * transforms). A metric the server did not send reads "not recorded", never 0. No engagement rate:
 * it needs extra phone activity (owner decision 2026-09-28).
 */

function Notice({ icon: Icon, title, children, testid }) {
  return (
    <section className="aurora-card p-5 sm:p-6" data-testid={testid}>
      <h2 className="flex items-center gap-2 font-heading text-base font-semibold">
        <Icon className="h-4 w-4 text-accent" aria-hidden />
        {title}
      </h2>
      <div className="mt-3 text-sm">{children}</div>
    </section>
  );
}

function Section({ icon: Icon, title, subtitle, children, testid, label }) {
  return (
    <section className="aurora-card min-w-0 p-4 sm:p-5" data-testid={testid} aria-label={label || title}>
      <h2 className="flex flex-wrap items-center gap-2 font-heading text-base font-semibold">
        <Icon className="h-4 w-4 text-primary" aria-hidden />
        {title}
      </h2>
      {subtitle && <p className="mt-1 text-xs text-muted-foreground text-pretty">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

const RANGE_OPTIONS = RANGES.map((r) => ({ value: r, label: `${r} days` }));

/** Under the page title: the span the range covers, when the profile was checked, how old the data is. */
function HeaderMeta({ stats, days }) {
  const checked = fmtServerDateTime(stats.lastStatsCheck);
  const newest = newestDay(stats);
  const age = newest ? daysBetween(stats.today, newest) : null;
  const counterToday = stats.actionsDay0 === stats.today;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid="stats-toolbar">
      {days.length > 0 && (
        <span className="inline-flex items-center gap-1.5" data-testid="stats-range-span">
          <CalendarDays className="h-3.5 w-3.5" aria-hidden />
          {fmtSpan(days[0], days[days.length - 1])}
        </span>
      )}
      {checked && (
        <span className="inline-flex items-center gap-1.5" data-testid="stats-last-checked">
          <Clock className="h-3.5 w-3.5" aria-hidden />
          Profile checked <span className="text-foreground">{checked}</span> <span>(server time)</span>
        </span>
      )}
      {stats.actionsDay0Known && (
        <span data-testid="stats-actions-day">
          Actions counted to <span className="text-foreground">{counterToday ? 'today (in progress)' : fmtDay(stats.actionsDay0, 'medium')}</span>
        </span>
      )}
      {age !== null && age >= 2 && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--warning)/.12)] px-2.5 py-1 font-medium text-[hsl(var(--warning))]"
          data-testid="stats-stale">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          Latest data is {age} days old
        </span>
      )}
    </div>
  );
}

const growthTable = (gp, noun) => ({
  caption: `${noun} by day`,
  columns: [
    { key: 'date', label: 'Date', render: (r) => fmtDay(r.date, 'long') },
    ...(gp.mode === 'count' ? [{ key: 'count', label: noun, align: 'right', render: (r) => fmtInt(r.count) }] : []),
    { key: 'change', label: 'Change', align: 'right', render: (r) => (r.change === null ? (r.seed ? 'first check' : 'not recorded') : fmtSigned(r.change)) },
  ],
  rows: [...gp.points].reverse(),
});

function GrowthCard({ gp, title, noun, icon, height, curve, proj, testid }) {
  const t = growthTable(gp, title);
  const days = gp.points.map((p) => p.date);
  const span = days.length ? fmtSpan(days[0], days[days.length - 1]) : null;
  const recordedNote = gp.recorded < days.length ? ` · ${gp.recorded} of ${plural(days.length, 'day')} with a change recorded` : '';
  let subtitle = null;
  if (gp.mode === 'count') subtitle = `The count at each daily profile check, ${span}${recordedNote}`;
  else if (gp.mode === 'net') subtitle = `Net change in ${noun} over ${span}, day by day (the exact count is not known yet)${recordedNote}`;
  const withProj = gp.mode === 'count' && proj && proj.line.length > 0;
  return (
    <ChartCard icon={icon} title={title} subtitle={subtitle} testid={testid}
      legend={withProj ? (
        <Legend testid={`${testid}-legend`} items={[
          { key: 'rec', label: title, color: 'var(--viz-line)', kind: 'line' },
          { key: 'est', label: `Estimate: the next ${plural(proj.line.length, 'day')} at the last ${plural(proj.windowDays, 'recorded day')}' pace`, color: 'var(--viz-line)', kind: 'dash' },
        ]} />
      ) : null}
      table={gp.mode === 'none' ? null : <DataTableView caption={t.caption} columns={t.columns} rows={t.rows} testid={`${testid}-datatable`} />}>
      {gp.mode === 'none' ? (
        <EmptyPanel testid={`${testid}-empty`}>
          {span && gp.track.present ? `No ${noun} change recorded in ${span}.` : `No ${noun} history recorded yet.`}
        </EmptyPanel>
      ) : (
        <GrowthChart gp={gp} noun={noun} yTitle={gp.mode === 'count' ? title : `Net change in ${noun}`} height={height}
          curve={curve} proj={withProj ? proj : null} testid={`${testid}-plot`} />
      )}
    </ChartCard>
  );
}

/** "Welcome DMs, Story views and Posts" */
const listText = (labels) => (labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`);

export default function SlotHistoryPanels({ slotName, renderHeader }) {
  const q = useQuery({
    queryKey: ['slotStats', slotName],
    queryFn: () => slotStatsApi.get(slotName).then((r) => r.data),
    retry: false,
  });
  const stats = useMemo(() => (q.data ? normaliseSlotStats(q.data) : null), [q.data]);
  const [range, setRange] = useState(30);
  const [metric, setMetric] = useState('all');
  // the types hidden from the stacked chart (kept across a range change and a type's tab)
  const [hiddenTypes, setHiddenTypes] = useState([]);

  const view = useMemo(() => {
    if (!stats || !stats.anyPresent) return null;
    const gDays = rangeDays(stats, range, 'growth');
    const aDays = rangeDays(stats, range, 'actions');
    const tDays = rangeDays(stats, range, 'all');
    const rows = actionRows(stats, aDays);
    const p = projection(stats, range);
    const weekly = range >= WEEKLY_FROM_RANGE;
    return {
      gDays, aDays, tDays, weekly,
      anyGrowth: ['followers', 'followings', 'posts'].some((k) => growthTrack(stats, k).present),
      followers: growthPoints(stats, 'followers', gDays),
      followings: growthPoints(stats, 'followings', gDays),
      posts: growthPoints(stats, 'posts', gDays),
      tiles: ['followers', 'followings', 'posts'].map((k) => headline(stats, k, gDays, range)),
      rows,
      summary: actionSummary(stats, rows),
      late: lateStarts(stats, aDays),
      tableLate: lateStarts(stats, tDays),
      totals: actionTotals(stats, range),
      table: dailyTable(stats, tDays),
      projection: p,
      projLine: p.ok ? { line: projectionLine(p, CHART_PROJECTION_DAYS[range] || 10), perDay: p.perDay, windowDays: p.windowDays } : null,
      ratios: rangeRatios(stats, tDays),
    };
  }, [stats, range]);

  const header = (extra) => (renderHeader ? renderHeader(extra || {}) : null);
  const r15 = r15UiEnabled();
  // r16 TT-6 (the TT-5 review's must-fix 10, design 2.6): Close Friends and the saved collection are Instagram features - their
  // cards are not drawn on a TikTok slot. The slot's platform comes from the TikTok card's own answer (the same query and cache:
  // GET /uiapi/slots/{slot}/tiktok-stats says applies:true for a TikTok slot); an older server (404) or a failed read keeps the
  // Instagram page exactly as it was. The two cards wait for that answer, so a TikTok slot never flashes them.
  const tt = useQuery({
    queryKey: ['tiktokStats', slotName],
    queryFn: () => slotStatsApi.tiktok(slotName).then((r) => r.data),
    retry: false,
  });
  // r16 TT-9 (review round 3 should-fix): with the server's TikTok stats switched off (the route answers 404 'not_enabled' - a server WITH
  // TikTok, its stats rolled back) the platform is read from the slot's settings answer ("platform", r16), so a TikTok slot still never
  // gets the Instagram-only cards. A route-missing 404 (an older server: no TikTok slot exists there) asks nothing more.
  const ttStatsOff = !!(tt.error && tt.error.response && tt.error.response.status === 404 && tt.error.response.data
    && tt.error.response.data.error === 'not_enabled');
  const plat = useQuery({
    queryKey: ['slotPlatform', slotName],
    queryFn: () => slotsApi.getSettings(slotName).then((r) => slotPlatform(r.data)),
    enabled: ttStatsOff,
    retry: false,
  });
  const platWaiting = ttStatsOff && plat.data === undefined && !plat.error;
  const tiktokSlot = !!(tt.data && typeof tt.data === 'object' && (tt.data.applies === true || tt.data.platform === 'TIKTOK'))
    || (ttStatsOff && plat.data === 'TIKTOK');
  const instagramCards = r15 && !tt.isLoading && !platWaiting && !tiktokSlot;
  // r16m (design 3.7): the Follow-back card - Instagram only (the same platform gate as the round-15 cards), right after the
  // actions-done ratios; when /stats has nothing to draw it sits directly under the page's main block instead.
  const followBack = r16mUiEnabled() && !tt.isLoading && !platWaiting && !tiktokSlot
    ? <FollowBackCard slotName={slotName} range={range} /> : null;
  // r16n: story shares by audience (Your story / Close Friends) - Instagram only, the same platform wait as the cards above
  const storyShares = r16nUiEnabled() && !tt.isLoading && !platWaiting && !tiktokSlot
    ? <StoryShareCard slotName={slotName} statsRaw={q.data} range={range} /> : null;

  let main;
  const qErr = q.error ? classifyR14Error(q.error) : null;
  if (q.isLoading) {
    main = <p className="text-sm text-muted-foreground">Loading statistics...</p>;
  } else if (qErr && qErr.kind === 'routeMissing') {
    main = (
      <Notice icon={Info} title="Per-slot history is not available on this server yet" testid="slot-stats-no-route">
        <p className="text-muted-foreground">
          This server has no <span className="font-mono">/slots/{'{slot}'}/stats</span> route
          ({qErr.status}). The daily history needs the round-14 server.
        </p>
      </Notice>
    );
  } else if (qErr && qErr.kind === 'notEnabled') {
    main = (
      <Notice icon={Info} title="Per-slot history is switched off on this server" testid="slot-stats-disabled">
        <p className="text-muted-foreground">{qErr.message || 'The server has the route, but its history part is switched off.'}</p>
      </Notice>
    );
  } else if (qErr && qErr.kind === 'slotNotFound') {
    main = (
      <Notice icon={Info} title="Slot not found" testid="slot-stats-no-slot">
        <p className="text-destructive">{qErr.message || `No slot ${slotName} on this account.`}</p>
      </Notice>
    );
  } else if (q.error) {
    main = (
      <Notice icon={Info} title="Statistics could not be loaded" testid="slot-stats-error">
        <p className="text-destructive">{r14ErrorText(q.error, 'The server did not answer.')}</p>
      </Notice>
    );
  } else if (!stats || !stats.anyPresent || !view) {
    main = (
      <Notice icon={Info} title="No statistics yet" testid="slot-stats-empty">
        <p className="text-muted-foreground">
          Stats appear after the slot&apos;s first day and its first daily profile check.
        </p>
      </Notice>
    );
  } else {
    const { gDays, aDays, tDays, weekly } = view;
    const counterIsToday = stats.actionsDay0 === stats.today;
    const counterDayHeader = counterIsToday ? 'Today' : fmtDay(stats.actionsDay0, 'medium');
    const counterStopped = daysBetween(stats.today, stats.actionsDay0) >= 2;
    const partial = partialDay(stats);
    const checked = fmtServerDateTime(stats.lastStatsCheck);
    const [fTile, gTile, pTile] = view.tiles;
    const aSpan = aDays.length ? fmtSpan(aDays[0], aDays[aDays.length - 1]) : null;
    const gSpan = gDays.length ? fmtSpan(gDays[0], gDays[gDays.length - 1]) : null;
    const tSpan = tDays.length ? fmtSpan(tDays[0], tDays[tDays.length - 1]) : null;

    // Actions: the metric tabs; "All actions" stacked by type, or one type alone.
    const summary = view.summary;
    const allRow = summary[0];
    const anyAction = allRow && allRow.recorded > 0;
    const allZero = anyAction && allRow.total === 0;
    const selected = summary.some((s) => s.key === metric) ? metric : 'all';
    const sel = summary.find((s) => s.key === selected) || allRow;
    // the stack's types, in METRICS order (bottom of the stack first) - the types recorded in the range
    const breakdown = summary.filter((s) => s.key !== 'all').map((s) => ({ key: s.key, label: s.label }));
    const metricKeys = breakdown.map((b) => b.key);
    const bars = actionBars(view.rows, selected, weekly, metricKeys);
    const selZero = sel && sel.recorded > 0 && sel.total === 0;
    const late = selected === 'all' ? view.late : [];
    const hasPartialBar = bars.some((b) => b.partial && b.value);
    const stoppedText = counterStopped && stats.actionsDay0Known
      ? ` The slot's counters have not moved on since ${fmtDay(stats.actionsDay0, 'medium')}.` : '';

    const allBars = actionBars(view.rows, 'all', weekly, metricKeys);
    const stack = actionStack(allBars, metricKeys, hiddenTypes, { weekly });
    const actionsTable = {
      caption: weekly ? 'Actions per week' : 'Actions per day',
      columns: [
        { key: 'date', label: weekly ? 'Week' : 'Date', render: (r) => (weekly ? fmtSpan(r.from, r.to) : fmtDay(r.date, 'long')) },
        ...breakdown.map((s) => ({ key: s.key, label: s.label, align: 'right', render: (r) => (r.parts[s.key] === null ? '-' : fmtInt(r.parts[s.key])) })),
        {
          key: 'total', label: 'Total', align: 'right',
          render: (r) => (r.value === null ? 'not recorded' : `${fmtInt(r.value)}${r.running ? ' (so far)' : ''}`),
        },
      ],
      rows: [...allBars].reverse(),
    };
    const cBars = changeBars(view.followers.points, weekly);
    const changeTable = {
      caption: weekly ? 'Weekly follower change' : 'Daily follower change',
      columns: [
        { key: 'date', label: weekly ? 'Week' : 'Date', render: (r) => (weekly ? fmtSpan(r.from, r.to) : fmtDay(r.date, 'long')) },
        {
          key: 'change', label: 'Change', align: 'right',
          render: (r) => (r.change === null ? (r.seed ? 'first check' : 'not recorded') : `${fmtSigned(r.change)}${weekly && r.recorded < 7 ? ` (${r.recorded} of 7 days)` : ''}`),
        },
        ...(view.followers.mode === 'count' ? [{ key: 'count', label: 'Followers', align: 'right', render: (r) => fmtInt(r.count) }] : []),
      ],
      rows: [...cBars].reverse(),
    };

    let actionsBody;
    if (!anyAction) {
      actionsBody = (
        <EmptyPanel testid="chart-actions-empty">
          {aSpan ? `No actions recorded in ${aSpan}.` : 'No actions recorded for this slot yet.'}{stoppedText}
        </EmptyPanel>
      );
    } else if (allZero) {
      actionsBody = (
        <EmptyPanel testid="chart-actions-empty">
          No actions in {aSpan}: every recorded day is 0.{stoppedText}
        </EmptyPanel>
      );
    } else {
      actionsBody = (
        <>
          <MetricTabs summary={summary} selected={selected} onSelect={setMetric} testid="chart-metric" />
          <div className="mt-4">
            {selected === 'all' ? (
              <ActionsStack stack={stack} series={breakdown} weekly={weekly} late={late} testid="stats-daily-chart"
                onToggle={(k) => setHiddenTypes(toggleHidden(stack.hidden, k, metricKeys))}
                onShowAll={() => setHiddenTypes([])} />
            ) : selZero ? (
              <EmptyPanel testid="chart-actions-metric-empty">
                No {sel.label.toLowerCase()} in {aSpan}: every recorded day is 0.
              </EmptyPanel>
            ) : (
              <ActionsChart bars={bars} metric={{ key: sel.key, label: sel.label }} avg={sel.avg} weekly={weekly}
                color={actionColor(sel.key)} testid="stats-daily-chart" />
            )}
          </div>
        </>
      );
    }
    const chartNotes = [];
    if (anyAction && !allZero && !selZero && hasPartialBar) {
      chartNotes.push(
        <p key="partial" data-testid="stats-partial-note">
          {weekly
            ? 'Lighter bars are incomplete weeks: the week holding today (so far), or fewer than 7 recorded days.'
            : 'The lighter bar is today so far: the day is not over, so it is left out of the averages.'}
        </p>,
      );
    }
    if (anyAction && !allZero && late.length) {
      late.forEach((l) => chartNotes.push(
        <p key={`late-${l.date}`} data-testid="stats-late-start-note">
          {listText(l.labels)} {l.labels.length === 1 ? 'is' : 'are'} recorded from {fmtDay(l.date, 'medium')}; the totals
          before that date do not include {l.labels.length === 1 ? 'it' : 'them'}.
        </p>,
      ));
    }
    const actionsFooter = [];
    if (!counterIsToday && stats.actionsDay0Known) {
      actionsFooter.push(
        <p key="counter" data-testid="stats-counter-day-note">
          The slot&apos;s counters are on {fmtDay(stats.actionsDay0, 'medium')}: it has not started a newer day since, so
          that column is that day&apos;s count and the 7, 30 and 90 day totals end on {fmtDay(stats.actionsDay0, 'medium')}.
        </p>,
      );
    }
    if (partial) {
      actionsFooter.push(
        <p key="avg">Avg / day counts complete days only: today&apos;s running count is left out.</p>,
      );
    }

    // Ratios: explain a range where no day holds both a follower change and an action count.
    const growthRec = view.followers.points.filter((pt) => pt.change !== null);
    const noOverlapWhy = growthRec.length && aDays.length
      ? `actions are recorded ${fmtSpan(aDays[0], aDays[aDays.length - 1])}, follower changes ${fmtSpan(growthRec[0].date, growthRec[growthRec.length - 1].date)}`
      : null;

    // The worker's own reading of the profile header, formatted (it is the value when there is no exact check).
    const cur = stats.current;
    let sourceNote = null;
    if (cur && cur.source === 'profileCheck') {
      sourceNote = `Counts from the daily profile check${checked ? ` (${checked}, server time)` : ''}.`;
    } else if (cur && cur.source === 'worker') {
      sourceNote = `As the worker last read the profile: ${cur.text.posts} posts · ${cur.text.followers} followers · ${cur.text.followings} following`
        + `${cur.approximate ? ' (Instagram rounds K and M figures)' : ''}. The daily profile check has not given exact counts yet.`;
    } else if (stats.countsSnapshot) {
      sourceNote = `The worker last read the profile header as "${stats.countsSnapshot}" (not a format this page can read).`;
    }

    main = (
      <>
        {(view.anyGrowth || stats.current) ? (
          <section aria-label="Headline figures" data-testid="growth-current">
            <div className="grid gap-3 md:grid-cols-3">
              <HeadlineTile icon={Users} label="Followers" h={fTile} testid="stats-tile-followers" />
              <HeadlineTile icon={UserPlus} label="Following" h={gTile} testid="stats-tile-followings" />
              <HeadlineTile icon={ImageIcon} label="Posts" h={pTile} testid="stats-tile-posts" />
            </div>
            {sourceNote && (
              <p className="mt-2 px-1 text-xs text-muted-foreground" data-testid="growth-current-source">{sourceNote}</p>
            )}
          </section>
        ) : null}

        {!view.anyGrowth ? (
          <Notice icon={TrendingUp} title="Account growth" testid="slot-stats-growth">
            <p className="text-muted-foreground" data-testid="growth-not-recorded">
              No daily growth has been recorded for this account yet. The daily profile check fills this in once it has run
              on two different days.
            </p>
          </Notice>
        ) : (
          <div className="space-y-4" data-testid="slot-stats-growth">
            <GrowthCard gp={view.followers} title="Followers" noun="followers" icon={TrendingUp} height={280}
              curve="linear" proj={view.projLine} testid="chart-followers" />
            <div className="grid gap-4 lg:grid-cols-2">
              <GrowthCard gp={view.followings} title="Following" noun="following" icon={UserPlus} height={190}
                curve="linear" testid="chart-followings" />
              <GrowthCard gp={view.posts} title="Posts" noun="posts" icon={ImageIcon} height={190}
                curve="stepAfter" testid="chart-posts" />
            </div>
            <div className="grid gap-4 xl:grid-cols-3">
              <ChartCard className="xl:col-span-2" icon={BarChart2} title={weekly ? 'Weekly follower change' : 'Daily follower change'}
                testid="chart-daily-change"
                subtitle={weekly
                  ? `Followers gained or lost per week (7-day periods ending on the dates shown), ${gSpan}`
                  : `Followers gained or lost between one daily profile check and the next, ${gSpan}`}
                legend={view.followers.mode === 'none' ? null : (
                  <Legend testid="chart-daily-change-legend" items={[
                    { key: 'gain', label: 'Gained', color: 'var(--viz-gain)' },
                    { key: 'loss', label: 'Lost', color: 'var(--viz-loss)' },
                    ...(weekly && cBars.some((b) => b.recorded > 0 && b.recorded < 7)
                      ? [{ key: 'part', label: 'Fewer than 7 days recorded', color: 'var(--viz-gain)', kind: 'faint' }] : []),
                  ]} />
                )}
                table={view.followers.mode === 'none' ? null : (
                  <DataTableView caption={changeTable.caption} columns={changeTable.columns} rows={changeTable.rows} testid="chart-daily-change-datatable" />
                )}>
                {view.followers.mode === 'none' ? (
                  <EmptyPanel>{gSpan ? `No follower change recorded in ${gSpan}.` : 'No follower history recorded yet.'}</EmptyPanel>
                ) : (
                  <DailyChangeChart gp={view.followers} noun="followers" weekly={weekly}
                    yTitle={weekly ? 'Followers gained / lost per week' : 'Followers gained / lost'} testid="chart-daily-change-plot" />
                )}
              </ChartCard>
              <section className="aurora-card min-w-0 p-4 sm:p-5" data-testid="stats-projection" aria-label="Followers projection">
                <h2 className="flex items-center gap-2 font-heading text-base font-semibold">
                  <Telescope className="h-4 w-4 text-primary" aria-hidden />
                  Followers projection
                  <span className="rounded-full border border-[hsl(var(--warning)/.4)] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--warning))]"
                    data-testid="projection-estimate-badge">
                    Estimate
                  </span>
                </h2>
                <div className="mt-3"><ProjectionBlock p={view.projection} range={range} /></div>
              </section>
            </div>
          </div>
        )}

        <ChartCard icon={Activity} title="Actions done" testid="slot-stats-actions"
          subtitle={aSpan
            ? `${weekly ? 'Per week' : 'Per day'}, ${aSpan}, counted on the slot's own day. `
              + `${selected === 'all' ? 'One colour per action type: click a type in the legend to hide or show it, or pick its tab to chart it alone.' : 'Pick a type to chart it alone.'}`
            : 'What the slot did each day, counted on the slot\'s own day'}
          table={anyAction ? (
            <DataTableView caption={actionsTable.caption} columns={actionsTable.columns} rows={actionsTable.rows} testid="chart-actions-datatable" />
          ) : null}
          after={(
            <div className="mt-5">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                <Table2 className="h-4 w-4 text-muted-foreground" aria-hidden />
                Totals
              </h3>
              <ActionTotalsTable totals={view.totals} dayHeader={counterDayHeader} range={range} />
            </div>
          )}
          footer={actionsFooter.length ? actionsFooter : null}>
          {actionsBody}
          {chartNotes.length > 0 && (
            <div className="mt-2 space-y-1 text-xs text-muted-foreground text-pretty" data-testid="stats-actions-notes">{chartNotes}</div>
          )}
        </ChartCard>

        {storyShares}

        <Section icon={Percent} title="Actions-done ratios" testid="slot-stats-ratios" label="Actions-done ratios"
          subtitle={tSpan ? `Over ${tSpan}, the selected ${range}-day range. Only days with both a follower change and an action count are counted${partial ? '; today\'s running count is left out' : ''}.` : null}>
          {view.ratios.length === 0 ? (
            <p className="text-sm text-muted-foreground">Needs both a followers history and an actions history.</p>
          ) : <RatioTiles ratios={view.ratios} noOverlapWhy={noOverlapWhy} />}
          <p className="mt-3 text-xs text-muted-foreground">
            There is no engagement rate here: measuring it needs extra activity on the phone.
          </p>
        </Section>

        {followBack}

        <Section icon={CalendarDays} title="Daily history" testid="slot-stats-daily" label="Daily history"
          subtitle={`Newest first · the count at each day's profile check, the change since the previous check, and the day's actions${tSpan ? ` · ${tSpan}` : ''}`
            + `${view.followers.mode === 'count' ? '' : ' (counts appear once the profile check has read them)'}`}>
          {view.table.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing recorded in the last {range} days.</p>
          ) : <DailyTable table={view.table} range={range} />}
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs text-muted-foreground" data-testid="growth-daily-note">
            <li>
              Followers, following and posts are dated by the daily profile check
              {stats.growthDay0 ? ` (latest ${checked || fmtDay(stats.growthDay0, 'medium')}, server time)` : ''}. A day&apos;s
              change runs from the previous check to that day&apos;s, so it mostly reflects the previous day&apos;s actions.
            </li>
            <li>
              The server stores one change per check date: when a day&apos;s check is missed, the next change covers both days
              and the older dates shift by one. The oldest day of a history is the first check, which has no change.
            </li>
            <li>
              Actions are dated by the slot&apos;s own day{stats.actionsDay0Known ? ` (latest ${fmtDay(stats.actionsDay0, 'medium')})` : ''} and
              {' '}count every action type recorded that day.
              {view.followers.mode === 'count' && view.table.summary.change.followers !== null
                ? ` Followers over the range: ${fmtSigned(view.table.summary.change.followers)} (${fmtRate(view.table.summary.average.followers)} a day).` : ''}
            </li>
            {view.tableLate.map((l) => (
              <li key={`late-${l.date}`} data-testid="daily-late-start-note">
                Before {fmtDay(l.date, 'medium')} the Actions column does not include {listText(l.labels)}
                {' '}({l.labels.length === 1 ? 'it is' : 'they are'} recorded from that day), so its daily average mixes both.
              </li>
            ))}
          </ul>
        </Section>
      </>
    );
  }

  const ready = !!(view && !q.error && !q.isLoading);
  return (
    <div className="stats-viz" data-testid="slot-history-panels">
      {header(ready ? {
        actions: <Segmented label="Time range" options={RANGE_OPTIONS} value={range} onChange={setRange} testidPrefix="stats-range" />,
        meta: <HeaderMeta stats={stats} days={view.tDays} />,
      } : {})}
      <div className="space-y-4">
        {main}
        {!view && !q.isLoading && followBack}
        {instagramCards && <CloseFriendsCard slotName={slotName} statsRaw={q.data} statsLoading={q.isLoading} range={range} />}
        {instagramCards && <SavedCollectionCard slotName={slotName} />}
        {/* r16 TT-3: a TikTok slot's own counters; draws nothing on an Instagram slot or an older server */}
        <TikTokStatsCard slotName={slotName} />
        <NotFoundTargetsCard slotName={slotName} />
      </div>
    </div>
  );
}
